import React, { useState } from 'react'
import { message, Table, TableColumnsType } from 'antd'
import { Box, IconButton, Typography } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import dayjs from 'dayjs'
import EditIcon from '@mui/icons-material/Edit'

import { IFilterMapped } from './AddressFilter'
import { ONLY_DATE_FORMAT, ONLY_TIME_FORMAT } from '../../../../../constants/global'
import { AddressFormModal } from './AddressFormModal'
import { axiosInstance } from '../../../../../hooks/useAxios'
import CircularLoading from '../../../../../components/CircularProgress'
import { IAddress } from '../../../../../types/address'
import Pagination from '../../../../../components/Pagination'
import { ERRORS_MESSAGE_MAPPER } from '../../../../../constants/errors'

interface IDistributionChannelsTableProps {
  isLoading: boolean
  data: IAddress[]
  onUpdateAddressListSuccess: () => void
  filter: IFilterMapped
  onFilterChange: (filter: IFilterMapped) => void
  total: number
}

const AddressTable = ({
                        isLoading,
                        data,
                        onUpdateAddressListSuccess,
                        filter,
                        onFilterChange,
                        total,
                      }: IDistributionChannelsTableProps) => {
  const [isModalEditAddress, setIsModalEditAddress] = useState<boolean>(false)
  const [addressEdit, setAddressEdit] = useState<IAddress>()
  const [isLoadingDelete, setIsLoadingDelete] = useState<boolean>(false)
  const onHandleDeleteDistributionChannel = async (address: IAddress) => {
    setIsLoadingDelete(true)
    try {
      await axiosInstance.delete(`/address/${address.objectId}`)
      message.success(`ลบที่อยู่ของ ${address.name} สำเร็จ`)
      onUpdateAddressListSuccess()
    } catch (e: any) {
      const messageError = ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] || 'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
    setIsLoadingDelete(false)
  }


  const columns: TableColumnsType<IAddress & { key: number }> = [
    {
      title: 'ลำดับ',
      dataIndex: 'numberRow',
      key: 'numberRow',
      align: 'center' as const,
      width: 80,
      render: (numberRow: number) => {
        return (
          <Box>
            <Typography variant="subtitle1" color="text.secondary" textAlign={'center'}>
              {numberRow}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'ชื่อ',
      dataIndex: 'name',
      key: 'name',
      align: 'center' as const,
      render: (name: string) => {
        return (
          <Box sx={{ minWidth: 150, textAlign: 'left' }}>
            <Typography variant="subtitle1" color="text.secondary">
              {name}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'ที่อยู่',
      dataIndex: 'address',
      key: 'address',
      align: 'center' as const,
      render: (_: undefined, address: IAddress) =>
        <Box sx={{ minWidth: 170, textAlign: 'left' }}>
          <Typography variant="subtitle2" color="text.secondary">
            {address.address} {address.subDistrict} {address.district} {address.province} {address.postcode}
          </Typography>
        </Box>,
    },
    {
      title: 'เบอร์โทร',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      align: 'center' as const,
      width: 80,
      render: (phoneNumber: string) =>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            {phoneNumber}
          </Typography>
        </Box>,
    },
    {
      title: 'วันที่เพิ่ม',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      align: 'center' as const,
      render: (createdAt: string) =>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            {dayjs(createdAt).format(ONLY_DATE_FORMAT)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {dayjs(createdAt).format(ONLY_TIME_FORMAT)}
          </Typography>
        </Box>,
    },
    {
      title: 'วันที่แก้ไข',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 160,
      align: 'center' as const,
      render: (updatedAt: string) =>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            {dayjs(updatedAt).format(ONLY_DATE_FORMAT)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {dayjs(updatedAt).format(ONLY_TIME_FORMAT)}
          </Typography>
        </Box>,
    },
    {
      dataIndex: '',
      key: 'action',
      width: 120,
      align: 'center' as const,
      render: (_: undefined, address: IAddress) =>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <IconButton onClick={() => {
            setIsModalEditAddress(true)
            setAddressEdit(address)
          }}>
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={() => onHandleDeleteDistributionChannel(address)}>
            <DeleteIcon />
          </IconButton>
        </Box>,
    },
  ]

  return (
    <>
      <AddressFormModal
        isOpen={isModalEditAddress}
        onUpdateAddressListSuccess={onUpdateAddressListSuccess}
        onClose={(visible: boolean) => setIsModalEditAddress(visible)}
        address={addressEdit}
      />
      <CircularLoading isLoading={isLoading || isLoadingDelete} />
      <Table
        size={'middle'}
        columns={columns}
        scroll={{ x: columns.reduce((pre, cur: any) => cur.width ? cur.width + pre : pre, 0) }}
        dataSource={data?.map((address: IAddress, index: number) => {
          return {
            ...address,
            key: index,
            numberRow: (index + 1 + ((filter.page || 0) - 1) * (filter.perPage || 0)),
          }
        })}
        bordered
        pagination={false}
      />
      <Pagination
        total={total}
        current={filter.page!}
        pageSize={filter.perPage!}
        onChange={(page, perPage) => onFilterChange({ ...filter, page, perPage })}
      />
    </>
  )
}

export default AddressTable
